import clsx from 'clsx';
import { forwardRef } from 'react';

import {
  type AriaDisabledProps,
  type DOMProps,
  type StylingProps,
  type MaskingProps,
  type WithChildren,
  type DataTestId,
} from '@dynatrace/strato-components/core';
import { _canReceiveDefaultTextFormat } from '@dynatrace/strato-components/core';
import { Text } from '@dynatrace/strato-components/typography';

import { LabelCSS } from './Label.sty.js';
import { useFormFieldContext } from '../../hooks/useFormFieldContext.js';

/**
 * Available options for the label size
 * @public
 */
export interface LabelProps
  extends WithChildren,
    DOMProps,
    StylingProps,
    DataTestId,
    MaskingProps,
    AriaDisabledProps {
  /** Defines the id of a labelable form related field. */
  htmlFor?: string;

  /**
   * If there is a wrapping form field then this label uses the required value
   * from the form field, otherwise it uses this required value. The label
   * has a visual style to indicate the associated field is a mandatory field.
   * @defaultValue false
   */
  required?: boolean;

  /**
   * If there is a wrapping form field then this label uses the disabled value
   * from the form field, otherwise it uses this disabled value. The label
   * has a visual style to indicate the associated field is disabled.
   * @defaultValue false
   */
  disabled?: boolean;
}

const SimpleLabelText = forwardRef<HTMLLabelElement, LabelProps>(
  (props, forwardedRef) => (
    <Text
      ref={forwardedRef}
      as="label"
      textStyle="small-emphasized"
      {...props}
    />
  ),
);

/**
 * The `Label` component allows users to add an associated text to another
 * form control by using the `FormField` component.
 * @public
 */
export const Label = /* @__PURE__ */ forwardRef<HTMLLabelElement, LabelProps>(
  (props, forwardedRef) => {
    const {
      id,
      children,
      htmlFor,
      disabled: propDisabled,
      required: propRequired,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'aria-disabled': ariaDisabledProp,
      ...remainingProps
    }: LabelProps = props;

    const {
      controlId: formFieldContextId,
      required,
      disabled,
      ariaDisabled,
    } = useFormFieldContext();

    const isStringChild = _canReceiveDefaultTextFormat(children);
    const Component = isStringChild ? SimpleLabelText : 'label';

    const labelFor = htmlFor || formFieldContextId;

    return (
      <Component
        htmlFor={labelFor}
        id={id}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        ref={forwardedRef}
        className={clsx(
          LabelCSS({
            disabled: disabled || propDisabled || ariaDisabled,
            // Only apply the required styles if we have string children, otherwise block level
            // elements in children will move the `*` required marker somewhere.
            required: isStringChild && (required ?? propRequired),
          }),
          consumerClassName,
        )}
        style={consumerStyle}
        {...remainingProps}
      >
        {children}
      </Component>
    );
  },
);
(Label as typeof Label & { displayName: string }).displayName = 'Label';
